import React, { useContext, useEffect, useState } from "react";
import apiClient from "../api/apiClient";
import { AuthContext } from "../context/authContext";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "../Components/ui/card";
import { Button } from "../Components/ui/button";
import { Badge } from "../Components/ui/badge";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "../utils/utils";
import { Phone, Award, Plus, Loader2, LayoutDashboard, Tag, Video, ChevronLeft, ChevronRight, Play, Eye, Calendar } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { format } from 'date-fns';

import DonorRegistrationCard from "../Components/donor/DonorRegistrationCard";
import ImpactStats from "../Components/donor/ImpactStats";
import DonationCard from "../Components/donation/DonationCard";
import OnboardingGuide from "../Components/OnboardingGuide";



const VideoCarousel = ({ videos }) => {
  const navigate = useNavigate();
  const [position, setPosition] = useState(0);
  const [failedUrls, setFailedUrls] = useState([]);

  const visibleVideos = (videos || []).filter(v => !failedUrls.includes(v.file_url));

  // Auto-advance dashboard carousel slide every 7 seconds
  useEffect(() => {
    if (visibleVideos.length <= 1) return;
    const interval = setInterval(() => {
      setPosition((prev) => (prev + 1) % visibleVideos.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [visibleVideos.length]);

  const handleNext = () => {
    setPosition((prev) => (prev + 1) % visibleVideos.length);
  };

  const handlePrev = () => {
    setPosition((prev) => (prev - 1 + visibleVideos.length) % visibleVideos.length);
  };

  if (visibleVideos.length === 0) return null;

  const activePosition = position >= visibleVideos.length ? Math.max(0, visibleVideos.length - 1) : position;
  const activeVideo = visibleVideos[activePosition];

  return (
    <div className="relative w-full overflow-hidden bg-gradient-to-r from-rose-500/10 via-amber-500/10 to-orange-500/10 dark:from-rose-950/20 dark:via-amber-950/10 dark:to-orange-950/20 p-6 md:p-8 rounded-3xl border border-rose-100 dark:border-rose-900/50 shadow-xl backdrop-blur-md">
      
      {/* Ambient Blur element */}
      <div className="absolute top-0 left-1/4 w-32 h-32 bg-rose-400/20 dark:bg-rose-600/10 rounded-full blur-2xl pointer-events-none"></div>
      
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-rose-100 dark:bg-rose-900/40 rounded-xl text-rose-600 dark:text-rose-400">
            <Video className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-black tracking-tight text-zinc-900 dark:text-white">
              Your Impact Highlights
            </h2>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 font-medium">Auto-looping proof videos of your contributions</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link to={createPageUrl("impact-highlights")}>
            <Button size="sm" variant="outline" className="border-rose-200 dark:border-rose-900/60 hover:bg-rose-50 dark:hover:bg-rose-900/20 text-rose-600 dark:text-rose-400 font-bold rounded-full text-xs shadow-sm flex items-center gap-1.5 h-8">
              Open Fullscreen Theater
            </Button>
          </Link>

          {visibleVideos.length > 1 && (
            <div className="flex gap-1.5">
              <Button onClick={handlePrev} size="icon" variant="ghost" className="h-8 w-8 rounded-full border border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-300">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button onClick={handleNext} size="icon" variant="ghost" className="h-8 w-8 rounded-full border border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-300">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="relative aspect-video max-h-[380px] w-full rounded-2xl overflow-hidden bg-black shadow-lg">
        <AnimatePresence mode="wait">
          <motion.div
            key={activePosition}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 cursor-pointer"
            onClick={() => navigate(createPageUrl(`DonationDetails/${activeVideo.donation_id?._id || activeVideo.donation_id}`))}
          >
            {activeVideo.proof_type === 'photo' ? (
              <img
                src={activeVideo.file_url}
                className="w-full h-full object-cover"
                alt="Impact proof"
                onError={() => setFailedUrls((prev) => [...prev, activeVideo.file_url])}
              />
            ) : (
              <video
                src={activeVideo.file_url}
                className="w-full h-full object-cover"
                muted
                playsInline
                loop
                autoPlay
                onError={(e) => {
                  console.warn("Video failed to load in DonorDashboard:", activeVideo.file_url);
                  if (activeVideo.file_url && activeVideo.file_url.includes('amazonaws.com')) {
                    e.target.src = 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4';
                    e.target.load();
                    e.target.play().catch(err => console.log("Autoplay blocked on fallback:", err.message));
                  } else {
                    setFailedUrls((prev) => [...prev, activeVideo.file_url]);
                  }
                }}
              />
            )}
            <div className="absolute top-4 left-4 z-10">
              <Badge className="bg-black/40 text-white border border-white/20 backdrop-blur-md px-3 py-1 font-bold text-xs uppercase tracking-wider">
                {activeVideo.donation_id?.donation_type || 'Donation'} Proof
              </Badge>
            </div>

            <div className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/5 transition-colors">
              <div className="p-3 bg-white/25 backdrop-blur-md rounded-full border border-white/30 text-white shadow-xl opacity-80 hover:opacity-100 transition-opacity">
                {activeVideo.proof_type === 'photo' ? (
                  <Eye className="w-6 h-6 text-white" />
                ) : (
                  <Play className="w-6 h-6 fill-white" />
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {visibleVideos.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-4">
          {visibleVideos.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setPosition(idx)}
              className={`h-2 rounded-full transition-all duration-300 ${
                activePosition === idx ? "w-6 bg-rose-500" : "w-2 bg-zinc-300 dark:bg-zinc-700"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DonorDashboard = () => {
  const { user } = useContext(AuthContext);
  // ... (rest of hook logic remains, assuming lines 19-49 match context) ...
  const location = useLocation();
  const navigate = useNavigate();
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    if (location.state?.flash) {
      setFlash(location.state.flash);
      navigate('.', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const { data: dashboardData, isLoading, refetch } = useQuery({
    queryKey: ['donorDashboard', user?.email],
    queryFn: async () => {
      const { data } = await apiClient.get('/users/dashboard');
      return data;
    },
    enabled: !!user,
  });

  if (isLoading || !dashboardData) {
    return (
      <div className="min-h-screen p-6 flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  const { needsRegistration, donor, stats, recentDonations, proofVideos } = dashboardData;

  if (needsRegistration) {
    return (
      <div className="min-h-screen p-6 bg-zinc-50 dark:bg-zinc-950">
        <div className="max-w-2xl mx-auto pt-12">
          <DonorRegistrationCard onComplete={refetch} />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 transition-colors duration-300 min-h-screen" id="tour-welcome">
      <OnboardingGuide />
      <div className="max-w-6xl mx-auto space-y-10">

        {/* Flash Messages */}
        {flash && (
          <div className={`p-4 rounded-xl shadow-sm border flex items-center gap-3 animate-in slide-in-from-top-2 ${flash.type === 'success' ? 'bg-emerald-50/80 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400' : 'bg-red-50/80 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'}`}>
            <div className={`w-2.5 h-2.5 rounded-full ${flash.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'} ring-4 ring-white dark:ring-zinc-900`} />
            <span className="font-medium">{flash.message}</span>
          </div>
        )}

        {/* Hero Section */}
        <div id="tour-hero" className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-rose-500 via-orange-400 to-amber-500 text-white shadow-2xl p-8 md:p-12">
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-white/20 rounded-full blur-3xl opacity-50 mix-blend-overlay"></div>
          <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-64 h-64 bg-black/10 rounded-full blur-3xl opacity-30 mix-blend-overlay"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20viewBox%3D%220%200%20200%20200%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cfilter%20id%3D%22noiseFilter%22%3E%3CfeTurbulence%20type%3D%22fractalNoise%22%20baseFrequency%3D%220.65%22%20numOctaves%3D%223%22%20stitchTiles%3D%22stitch%22%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20filter%3D%22url%28%23noiseFilter%29%22%2F%3E%3C%2Fsvg%3E')] opacity-20 brightness-100"></div>

          <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start justify-between gap-8 text-center md:text-left">
            <div className="space-y-4 max-w-xl">
              <Badge className="bg-white/20 backdrop-blur-md border-white/20 text-white px-3 py-1 text-xs uppercase tracking-widest font-bold shadow-sm">
                {format(new Date(), 'MMMM do, yyyy')}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">
                Welcome back, <br /> {(user?.full_name || user?.name || 'Donor').split(' ')[0]}!
              </h1>
              <p className="text-orange-50 text-lg md:text-xl font-medium max-w-md mx-auto md:mx-0 leading-relaxed">
                Your generosity is changing lives. Ready to make another impact today?
              </p>
              <div className="pt-4 flex flex-wrap gap-4 justify-center md:justify-start">
                <Link to={createPageUrl("CreateDonation")}>
                  <Button id="tour-create-btn" size="lg" className="h-12 px-8 bg-white text-orange-600 hover:bg-orange-50 font-bold rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-base border-2 border-transparent">
                    <Plus className="w-5 h-5 mr-2" />
                    New Donation
                  </Button>
                </Link>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 flex flex-col items-center gap-4 min-w-[240px] shadow-inner text-white">
              <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-white to-orange-100 p-1 shadow-lg">
                <div className="w-full h-full rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm border-2 border-white/50">
                  <span className="text-2xl font-bold text-orange-600">{(user?.full_name || user?.name || 'D').charAt(0)}</span>
                </div>
              </div>
              <div className="text-center">
                <h3 className="font-bold text-lg">{donor.name}</h3>
                <p className="text-orange-100 text-sm">{donor.email}</p>
              </div>
              <div className="w-full h-px bg-white/20"></div>
              <div className="flex gap-4 text-xs font-medium text-orange-50">
                <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {donor.phone_number || '--'}</span>
                <span className="text-white/40">|</span>
                <span className="font-mono text-[10px]">ID: {donor._id}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Needed: Schedule Delivery for Approved Donations */}
        {(() => {
          const unscheduledDonations = (recentDonations || []).filter(d => 
            d.status === 'pending' && !d.scheduled_delivery && d.donation_type === 'garments'
          );
          if (unscheduledDonations.length === 0) return null;

          return (
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-6 rounded-3xl shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4 border border-blue-400/20 backdrop-blur-md animate-in slide-in-from-top-2 duration-300">
              <div className="flex items-center gap-4 text-center sm:text-left">
                <div className="p-3 bg-white/20 rounded-2xl">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Action Required: Schedule Delivery</h3>
                  <p className="text-sm text-blue-100 mt-0.5">
                    You have {unscheduledDonations.length} approved donation{unscheduledDonations.length > 1 ? 's' : ''} waiting for a delivery/drop-off schedule.
                  </p>
                </div>
              </div>
              <Link to={`/donations/${unscheduledDonations[0]._id}`}>
                <Button className="bg-white hover:bg-zinc-100 text-blue-600 font-bold px-6 py-2.5 rounded-full shadow-md transition-all text-sm whitespace-nowrap">
                  Schedule Now
                </Button>
              </Link>
            </div>
          );
        })()}

        {/* Impact Stats Section */}
        <div id="tour-stats" className="pt-4">
          <div className="flex items-center gap-2 mb-6 px-2">
            <div className="p-2 bg-rose-100 dark:bg-rose-900/30 rounded-lg text-rose-600 dark:text-rose-400">
              <Award className="w-5 h-5" />
            </div>
            <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Your Impact</h2>
          </div>
          <ImpactStats stats={stats} />
        </div>



        {/* Your Impact Highlights */}
        {proofVideos && proofVideos.length > 0 && (
          <VideoCarousel videos={proofVideos} />
        )}

        {/* Recent Donations */}
        <div className="pt-4">
          <div className="flex items-center gap-2 mb-6 px-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
              <LayoutDashboard className="w-5 h-5" />
            </div>
            <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Recent History</h2>
          </div>

          <div id="tour-history-link" className="grid gap-4">
            {recentDonations && recentDonations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recentDonations.map(donation => (
                  <DonationCard key={donation._id} donation={donation} className="hover:border-zinc-300 transition-colors" />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white dark:bg-zinc-900 rounded-2xl border border-dashed border-zinc-200 dark:border-zinc-800">
                <div className="p-4 bg-zinc-50 dark:bg-zinc-800 rounded-full inline-block mb-4">
                  <Plus className="w-8 h-8 text-zinc-400 dark:text-zinc-500" />
                </div>
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">No donations yet</h3>
                <p className="text-zinc-500 dark:text-zinc-400 text-sm mt-1 mb-6 max-w-sm mx-auto">
                  Your journey to making a difference starts here. Create your first donation request today.
                </p>
                <Link to={createPageUrl("CreateDonation")}>
                  <Button variant="outline" className="border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800">
                    Start Donation
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonorDashboard;